import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Grid,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  Add,
  Visibility,
  Edit,
  People,
  TrendingUp,
  Settings,
  Search,
  FilterList,
  Star,
  StarBorder,
  Block,
  CheckCircle,
  Cancel,
  MoreVert,
  Menu,
  ChevronLeft,
  ChevronRight,
  Analytics,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { getGroupBuyPools, getGroupBuyPoolDetail, updateGroupBuyPool, bulkActionGroupBuyPools } from '../../services/groupBuyService';
import CustomPagination from '../../components/common/CustomPagination';
import { getGroupBuyColumns } from '../../components/groupbuy/columns';
import PoolDetailsModal from '../../components/groupbuy/PoolDetailsModal';
import EditPoolModal from '../../components/groupbuy/EditPoolModal';

const InTransitPools = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  // State management
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 0
  });

  // Filters (removed status filter, set to 'in_transit' by default)
  const [filters, setFilters] = useState({
    status: 'in_transit', // Fixed status for this page
    featured: '',
    search: '',
    date_from: '',
    date_to: ''
  });

  // UI state
  const [selectedPools, setSelectedPools] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewModal, setViewModal] = useState({ open: false, pool: null });
  const [editModal, setEditModal] = useState({ open: false, pool: null });

  useEffect(() => {
    loadPools();
  }, [pagination.page, filters]);

  const loadPools = async () => {
    try {
      setLoading(true);
      const response = await getGroupBuyPools({
        ...filters,
        page: pagination.page,
        page_size: pagination.page_size
      });

      setPools(response.pools || []);
      setPagination({
        page: response.page || 1,
        page_size: response.page_size || 20,
        total_count: response.total_count || 0,
        total_pages: response.total_pages || 0
      });
    } catch (error) {
      console.error('Error loading pools:', error);
      showSnackbar(error.message || 'Failed to load pools', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewPool = (pool) => {
    setViewModal({ open: true, pool });
  };

  const handleCloseViewModal = () => {
    setViewModal({ open: false, pool: null });
  };

  const handleEditPool = (pool) => {
    setEditModal({ open: true, pool });
  };

  const handleCloseEditModal = () => {
    setEditModal({ open: false, pool: null });
  };

  const handleFeatureToggle = async (poolId, isFeatured) => {
    try {
      setUpdating(true);
      await updateGroupBuyPool(poolId, { is_featured: !isFeatured });
      showSnackbar(`Pool ${!isFeatured ? 'featured' : 'unfeatured'} successfully`);
      loadPools();
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedPools.length === 0) return;

    try {
      setUpdating(true);
      const response = await bulkActionGroupBuyPools(selectedPools, action);

      if (response.success) {
        showSnackbar(`${action} action completed for ${selectedPools.length} pools`);
        setSelectedPools([]);
        loadPools();
      } else {
        showSnackbar(response.message || 'Bulk action failed', 'error');
      }
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'closed': return 'warning';
      case 'purchased': return 'info';
      case 'in_warehouse': return 'primary';
      case 'in_transit': return 'secondary';
      case 'in_nigeria': return 'info';
      case 'fulfilled': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const columns = getGroupBuyColumns({
    selectedPools,
    setSelectedPools,
    handleViewPool,
    handleEditPool,
    handleFeatureToggle,
    updating
  });

  // Old columns definition removed - now using reusable component

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            In Transit Pools
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage pools where shipments have left China and are in transit
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadPools}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<TrendingUp />}
            onClick={() => navigate('/admin/group-buy/analytics')}
          >
            Analytics
          </Button>
          <Button
            variant="contained"
            startIcon={<Settings />}
            onClick={() => navigate('/admin/group-buy/settings')}
          >
            Settings
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>Filters</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Featured</InputLabel>
                <Select
                  value={filters.featured}
                  label="Featured"
                  onChange={(e) => handleFilterChange('featured', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Featured</MenuItem>
                  <MenuItem value="false">Not Featured</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Pool title or creator..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  endAdornment: <Search />,
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedPools.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'action.selected' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2">
                {selectedPools.length} pool(s) selected
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => handleBulkAction('feature')}
                disabled={updating}
              >
                Feature
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleBulkAction('cancel')}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedPools([])}
              >
                Clear Selection
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            overflowX: 'auto',
            minWidth: 1200,
          }}>
            {!loading && pools.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Typography variant="h6" color="text.secondary">
                  No pools in transit found
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={pools.map((pool, index) => ({
                  ...pool,
                  sn: (pagination.page - 1) * pagination.page_size + index + 1
                }))}
                columns={columns}
                disableSelectionOnClick
                loading={loading}
                hideFooterPagination
                hideFooter
                getRowSpacing={() => ({ top: 2, bottom: 2 })}
                sx={{
                  border: 'none',
                  minWidth: 1200,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: '1rem'
                  },
                  '& .MuiDataGrid-row': {
                    marginBottom: '4px',
                  },
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={pagination.total_pages}
          rowsPerPage={pagination.page_size}
          onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          onRowsPerPageChange={(newRowsPerPage) => {
            setPagination(prev => ({
              ...prev,
              page_size: newRowsPerPage,
              page: 1
            }));
          }}
        />
      </Box>

      {/* Pool Details Modal */}
      <PoolDetailsModal
        open={viewModal.open}
        pool={viewModal.pool}
        onClose={handleCloseViewModal}
      />

      {/* Edit Pool Modal */}
      <EditPoolModal
        open={editModal.open}
        pool={editModal.pool}
        onClose={handleCloseEditModal}
        onSave={loadPools}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InTransitPools;